import { errorForm, navigation } from '@origin-digital/reporting-client';
import { AxiosPromise } from 'axios';
import * as React from 'react';
import { ChangeEvent, FocusEvent, FormEvent } from 'react';
import { AccountResponse } from 'ts/services/find-account/AccountResponse';
import { AccountNumberView } from './AccountNumberView';
import { Content } from './Interfaces';
import { onlyNumeric } from '@origin-digital/normalize';

interface AccountNumberPageProps {
  history: any;
  nextAction: (email: string) => void;
  errorPageAction: (statusCode: number) => void;
  findAccountAction: (
    lastname: string,
    dob: string,
    postcode: string,
    accountNumber: string,
    userProvidedEmail?: string,
  ) => AxiosPromise<AccountResponse>;
  lastName: string;
  dob: string;
  postcode: string;
  userProvidedEmail?: string;
  content: Content;
}

class AccountNumberPage extends React.PureComponent<AccountNumberPageProps> {
  public state = {
    accountNumber: '',
    accountNumberError: '',
    spinner: false,
    alertBoxError: '',
  };

  public componentDidMount() {
    navigation('screen', {
      currentUri: '',
      friendlyUri: this.props.content.analytics.navigation.friendlyUri,
    });
  }

  public render() {
    return (
      <AccountNumberView
        content={this.props.content}
        onSubmit={this.onSubmit}
        onAccountNumberChange={this.onAccountNumberChange}
        onAccountNumberBlur={this.onAccountNumberBlur}
        accountNumberError={this.state.accountNumberError}
        history={this.props.history}
        spinner={this.state.spinner}
        alertBoxError={this.state.alertBoxError}
        accountNumberTransform={this.accountNumberTransform}
      />
    );
  }

  private accountNumberTransform = (value: string, prevValue: string) => {
    // only numbers no spaces
    const onlyNumbers = onlyNumeric(value, prevValue);
    // limit characters to 12
    return onlyNumbers.substring(0, 12);
  };

  private onAccountNumberBlur = (e: FocusEvent<HTMLInputElement>) => {
    this.checkValidAccountNumber();
  };

  private checkValidAccountNumber = (): boolean => {
    const isValidAccountNumber = this.state.accountNumber.length === 12;
    if (!isValidAccountNumber) {
      this.setState({
        accountNumberError: 'Your account number should be 12 digits',
      });
    }
    return isValidAccountNumber;
  };

  private onAccountNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
    this.setState({
      accountNumber: e.target.value,
      accountNumberError: '',
    });
  };

  private onSubmit = async (e: FormEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!this.checkValidAccountNumber()) {
      return;
    }

    this.setState({ spinner: true });
    const { lastName, dob, postcode, userProvidedEmail } = this.props;
    try {
      const accountsResponse = await this.props.findAccountAction(
        lastName,
        dob,
        postcode,
        this.state.accountNumber,
        userProvidedEmail,
      );
      this.props.nextAction(accountsResponse.data.email);
    } catch (error) {
      this.setState({ spinner: false });
      const { response = {} } = error || {};
      response.status === 404
        ? this.handleNoAccount()
        : this.props.errorPageAction(response.status);
    }
  };

  private handleNoAccount = () => {
    const { formName, errorMessage, errorCode } = this.props.content.analytics.errorForm;
    errorForm({
      formName,
      errorMessage,
      errorCode,
    });
    this.setState({
      alertBoxError: this.props.content.copy.alertBoxText,
    });
  };
}

export { AccountNumberPage };
